import React, { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Input,
  Button,
  Row,
  Col,
  FormGroup,
  FormFeedback,
  Label,
} from 'reactstrap';
import Icon from '@mdi/react';
import { mdiWindowClose } from '@mdi/js';
import { useForm, Controller } from 'react-hook-form';
import moment from 'moment';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { plannedEventValidationSchema, populateFormData } from './constants';
import {
  saveUpdateEvent,
  deleteEvent,
} from '../../../../../store/apis/eventService';
import { useConfirmationModal } from '../../../../hooks';
import { TimePicker, DatePicker } from '../../../../Common';

import './EventFormCanvasContent.scss';

export default function EventFormCanvasContent({
  data = null,
  eventData = null,
  type = 'create',
  handleCanvasClose,
}) {
  const queryClient = useQueryClient();
  const { open, ConfirmationModal } = useConfirmationModal();

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isValid },
    trigger,
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(plannedEventValidationSchema),
    mode: 'onChange',
  });

  const saveUpdateEventMutation = useMutation({
    mutationFn: saveUpdateEvent,
    onSuccess: (res, payload) => {
      if (payload.flag === 'insert') {
        toast.success('Planned Event Created Successfully', {
          className: 'toast-success',
        });
      } else if (payload.flag === 'update') {
        toast.success('Planned Event Updated Successfully', {
          className: 'toast-success',
        });
      }
      queryClient.invalidateQueries(['eventDetails']);
      handleCanvasClose();
    },
  });

  const deleteEventMutation = useMutation({
    mutationFn: deleteEvent,
    onSuccess: () => {
      toast.success('Planned Event Deleted Successfully', {
        className: 'toast-success',
      });
      queryClient.invalidateQueries(['eventDetails']);
      handleCanvasClose();
    },
  });

  const onSubmit = async (values, flag) => {
    const start = `${values.startDate}T${values.startTime}:00`;
    const end = `${values.endDate}T${values.endTime}:00`;
    const payload = {
      subject: values.subject,
      startDate: start,
      endDate: end,
      startTime: start,
      endTime: end,
      customer: values.customer,
      comments: values.comments,
    };

    if (type === 'create' && flag === 'insert') {
      await saveUpdateEventMutation.mutateAsync({
        ...payload,
        eventId: 0,
        flag,
      });
    }
    if (type === 'update' && flag === 'update') {
      await saveUpdateEventMutation.mutateAsync({
        ...payload,
        eventId: eventData.EventID,
        flag,
      });
    }
    if (type === 'update' && flag === 'delete') {
      await deleteEventMutation.mutateAsync({
        ...payload,
        eventId: eventData.EventID,
        flag,
      });
    }
  };

  useEffect(() => {
    if (type === 'create' && data) {
      setValue(
        'startDate',
        data?.start ? moment(data?.start).format('YYYY-MM-DD') : '',
      );
      setValue(
        'endDate',
        data?.end ? moment(data?.end).format('YYYY-MM-DD') : '',
      );
      setValue(
        'startTime',
        data?.start ? moment(data?.start).format('HH:mm') : '',
      );
      setValue('endTime', data?.end ? moment(data?.end).format('HH:mm') : '');
    } else if ((type === 'update' || type === 'view') && eventData && data) {
      const populated = populateFormData(eventData);
      reset(populated);
      trigger();
    }
  }, [data, type, eventData]);

  const generateTitle = () => {
    if (type === 'create') return 'Create Planned Event';
    if (type === 'update') return 'Update Planned Event';
    if (type === 'view') return 'System Event';
    return '';
  };

  const startTime = watch('startTime');
  const endTime = watch('endTime');
  useEffect(() => {
    if (startTime || endTime) {
      trigger(['startTime', 'endTime']);
    }
  }, [startTime, endTime, trigger]);

  return (
    <div className="event-form-canvas d-flex flex-column">
      <div className="event-form-header d-flex justify-content-between align-items-center ps-3 pe-2 py-3">
        <span className="event-form-title">{generateTitle()}</span>
        <Button
          className="close-button"
          onClick={handleCanvasClose}
          color="link"
          title="Close"
        >
          <Icon path={mdiWindowClose} size={1} />
        </Button>
      </div>
      <div className="event-form-body p-3 flex-grow-1">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row className="d-flex flex-wrap">
            <Col md={12} className="mb-2">
              <Label htmlFor="subject" className="my-1">
                Subject <span className="text-danger">*</span>
              </Label>
              <Controller
                name="subject"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    invalid={!!errors.subject}
                    placeholder="Enter Subject"
                    disabled={type === 'view'}
                    className="external-filter-input"
                    title="Enter a clear subject or title for the event. This will appear in your activity calendar."
                  />
                )}
              />
              <FormFeedback>{errors.subject?.message}</FormFeedback>
            </Col>
            <Col md={6} className="mb-2">
              <Label className="my-1">
                Start Date <span className="text-danger">*</span>
              </Label>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    disabled={type === 'view'}
                    className={`external-filter-input ${errors.startDate ? 'is-invalid' : ''}`}
                    error={!!errors.startDate}
                    title="Select the date when the event will begin."
                  />
                )}
              />
              {errors.startDate && (
                <div className="invalid-feedback d-block">
                  {errors.startDate.message}
                </div>
              )}
            </Col>
            <Col md={6} className="mb-2">
              <Label className="my-1">
                End Date <span className="text-danger">*</span>
              </Label>
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    disabled={type === 'view'}
                    className={`external-filter-input ${errors.endDate ? 'is-invalid' : ''}`}
                    error={!!errors.endDate}
                    title="Select the date when the event will end. Must be same or after the start date."
                  />
                )}
              />
              {errors.endDate && (
                <div className="invalid-feedback d-block">
                  {errors.endDate.message}
                </div>
              )}
            </Col>
            <Col md={6} className="mb-2 mt-1">
              <FormGroup check className="form-check-inline">
                <Controller
                  name="allDay"
                  control={control}
                  render={({ field }) => (
                    <Input
                      type="checkbox"
                      checked={field.value}
                      disabled={type === 'view'}
                      onChange={e => {
                        const isChecked = e.target.checked;
                        field.onChange(isChecked);
                        setValue('startTime', isChecked ? '08:00' : '');
                        setValue('endTime', isChecked ? '17:00' : '');
                        trigger(['startTime', 'endTime']);
                      }}
                      title="Check this if the event lasts the entire day with no specific start or end time. Will default to 8:00AM to 5:00PM"
                    />
                  )}
                />
                <Label className="form-check-label" htmlFor="allDay">
                  All Day
                </Label>
              </FormGroup>
            </Col>
          </Row>

          <Row className="d-flex flex-wrap">
            <Col md={6} className="mb-2">
              <Label className="my-1">
                Start Time <span className="text-danger">*</span>
              </Label>
              <Controller
                name="startTime"
                control={control}
                render={({ field }) => (
                  <TimePicker
                    {...field}
                    interval={15}
                    disabled={getValues('allDay') || type === 'view'}
                    className={`mpi-time-picker external-filter-input ${errors.startTime ? 'is-invalid' : ''} time-picker-pointer`}
                    error={!!errors.startTime}
                    title="Enter the time the event begins, if not an all-day event."
                  />
                )}
              />
              {errors.startTime && (
                <div className="invalid-feedback d-block">
                  {errors.startTime.message}
                </div>
              )}
            </Col>
            <Col md={6} className="mb-2">
              <Label className="my-1">
                End Time <span className="text-danger">*</span>
              </Label>
              <Controller
                name="endTime"
                control={control}
                render={({ field }) => (
                  <TimePicker
                    {...field}
                    interval={15}
                    disabled={getValues('allDay') || type === 'view'}
                    className={`mpi-time-picker external-filter-input ${errors.endTime ? 'is-invalid' : ''} time-picker-pointer`}
                    error={!!errors.endTime}
                    title="Enter the time the event ends. Should be after the start time."
                  />
                )}
              />
              {errors.endTime && (
                <div className="invalid-feedback d-block">
                  {errors.endTime.message}
                </div>
              )}
            </Col>
            <Col md={12} className="mb-2">
              <Label htmlFor="customer" className="my-1">
                Customer
              </Label>
              <Controller
                name="customer"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    invalid={!!errors.customer}
                    disabled={type === 'view'}
                    placeholder="Enter Customer"
                    className="external-filter-input"
                    title="Specify the customer associated with the event, if applicable."
                  />
                )}
              />
              <FormFeedback>{errors.customer?.message}</FormFeedback>
            </Col>
            <Col md={12} className="mb-2">
              <Label className="my-1">Comments</Label>
              <Controller
                name="comments"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type="textarea"
                    disabled={type === 'view'}
                    placeholder="Enter Comments"
                    className="external-filter-input"
                    title="Add any extra notes or details about the event for future reference."
                  />
                )}
              />
            </Col>
          </Row>
        </form>
      </div>

      <div className="event-form-footer offcanvas-footer p-3">
        {type === 'create' && (
          <Row className="w-100">
            <Col md={6}>
              <Button
                onClick={handleCanvasClose}
                className="custom-light-btn w-100"
              >
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button
                onClick={() =>
                  handleSubmit(values => onSubmit(values, 'insert'))()
                }
                className="custom-btn w-100"
                disabled={!isValid}
              >
                Submit
              </Button>
            </Col>
          </Row>
        )}
        {type === 'update' && (
          <Row className="w-100">
            <Col md={6}>
              <Button
                onClick={() => {
                  open({
                    title: 'Delete Planned Event',
                    message:
                      'Are you sure you want to delete this planned event?',
                    onConfirm: handleSubmit(values =>
                      onSubmit(values, 'delete'),
                    ),
                    showCancelButton: true,
                  });
                }}
                className="custom-danger-btn w-100"
              >
                Delete
              </Button>
            </Col>
            <Col md={6}>
              <Button
                onClick={() =>
                  handleSubmit(values => onSubmit(values, 'update'))()
                }
                className="custom-btn w-100"
                disabled={!isValid}
              >
                Update
              </Button>
            </Col>
            <ConfirmationModal />
          </Row>
        )}
      </div>
    </div>
  );
}
